import { ArrowUp, ChevronRightIcon, Loader2, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import Header from '@/components/header'
import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import productService from '@/services/product-service'
import { ColumnDef } from '@tanstack/react-table'
import { useMutation } from 'react-query'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'

const searchableColumns = [
	{ label: 'Titel', value: 'title' },
	{ label: 'Marke', value: 'brand_name' },
	{ label: 'Regal', value: 'stand_id' },
]

export const Search = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [searchValue, setSearchValue] = useState('')
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState<string>('title')
	const [showScrollTop, setShowScrollTop] = useState(false)
	const [expandedRows, setExpandedRows] = useState<number[]>([])

	const { data, isLoading, mutate } = useMutation(
		['fetch-products', searchValue, page, rowsPerPage],
		({
			searchValue,
			selectedColumn,
			page,
			rowsPerPage,
		}: {
			searchValue: string
			selectedColumn: string
			page: number
			rowsPerPage: number
		}) => {
			if (selectedColumn === 'stand_id' && isNaN(Number(searchValue))) {
				throw new Error('Stand Id must be number')
			} else {
				return productService.searchProducts(searchValue, selectedColumn, page, rowsPerPage)
			}
		},
		{
			onError: (error: any) => {
				toast.error(error?.message || 'Error occured while searching')
			},
		},
	)

	useEffect(() => {
		const handleScroll = () => {
			setShowScrollTop(window.scrollY > 300)
		}

		window.addEventListener('scroll', handleScroll)
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const columns: ColumnDef<ProductSearch>[] = [
		{
			accessorKey: 'title',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Titel" />,
			cell: ({ row }) => <div className="text-left max-w-[300px]">{row.getValue('title')}</div>,
		},
		{
			id: 'store',
			accessorFn: (row: ProductSearch) => row.store?.name || '—',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Store" />,
			cell: ({ row }) => {
				const store = row.original.store
				const ref = store?.slug || store?.uuid
				if (!store?.name || !ref) {
					return <div className="text-left">{store?.name || '—'}</div>
				}
				return (
					<Link
						to={`/stores/${ref}`}
						className="text-left text-secondary font-semibold hover:underline">
						{store.name}
						{store.city ? ` · ${store.city}` : ''}
					</Link>
				)
			},
		},
		{
			accessorKey: 'brand',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Marke" />,
			cell: ({ row }) => <div className="text-left">{row.getValue('brand')}</div>,
		},

		{
			accessorKey: 'quantity',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Menge" />,
		},
		{
			accessorKey: 'price',
			accessorFn: (row: ProductSearch) => `€${row.price}`,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'stand_id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Regal Nummer" />,
		},
	]

	const MobileProductsTable = () => {
		return (
			<Table className="table-fixed">
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">Titel</div>
						</TableHead>
						<TableHead
							align="center"
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">Store</div>
						</TableHead>
						<TableHead
							align="center"
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">Regal</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{data?.data.map((product, index) => (
						<React.Fragment key={product.stand_id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(index) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(index)}>
								<TableCell
									align="center"
									className={cn('text-xs text-left', {
										'text-muted-foreground': !expandedRows.includes(index),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(index)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{product.title}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(index),
									})}>
									{product.store?.slug || product.store?.uuid ? (
										<Link
											to={`/stores/${product.store.slug || product.store.uuid}`}
											className="text-secondary font-semibold"
											onClick={e => e.stopPropagation()}>
											{product.store.name}
										</Link>
									) : (
										product.store?.name || '—'
									)}
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(index),
									})}>
									{product.stand_id}
								</TableCell>
							</TableRow>
							{expandedRows.includes(index) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(index) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Store</h6>
												<p className="text-secondary text-xs font-medium">
													{product.store?.name || '—'}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Marke</h6>
												<p className="text-secondary text-xs font-medium">{product.brand}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Menge</h6>
												<p className="text-secondary text-xs font-medium">{product.quantity}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
												<p className="text-secondary text-xs font-medium">€{product.price}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Regal Nummer
												</h6>
												<p className="text-secondary text-xs font-medium">{product.stand_id}</p>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="bg-landing-page min-h-screen bg-cover bg-no-repeat">
			<div className="max-w-[150rem] mx-auto">
				<Header currentPage="search" />
				<div className="mt-5 lg:mt-[90px] flex items-center flex-col gap-3 lg:gap-2">
					<div className="flex flex-col items-center mx-4 sm:mx-5 lg:mx-20 gap-3 lg:gap-2">
						<h1 className="text-secondary text-center text-lg sm:text-xl lg:text-3xl font-extrabold">
							Hier kannst du nach bestimmten Artikeln, Marken und Regalen suchen.
						</h1>
						<p className="text-center text-sm lg:text-base text-secondary/80">
							Achtung: Die Artikelsuche dient zur Orientierung. Da unser Sortiment ständig wechselt,
							können angezeigte Artikel zwischenzeitlich bereits verkauft oder noch gar nicht von
							den Verkäuferinnen und Verkäufern im Laden aufgehangen worden sein. Eine
							Verfügbarkeitsgarantie besteht nicht.
						</p>
					</div>
					<div className="flex max-lg:flex-col w-full gap-3 max-lg:px-4 sm:max-lg:px-6 lg:max-w-2/3 mt-3 items-center">
						<Select
							value={selectedColumn}
							onValueChange={value => {
								setSelectedColumn(value)
							}}>
							<SelectTrigger className="h-10 border-black/20 text-xs w-full lg:basis-[20%]">
								<SelectValue placeholder="Kategorie" />
							</SelectTrigger>
							<SelectContent>
								{searchableColumns.map(column => (
									<SelectItem key={column.value} value={column.value}>
										{column.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className="relative w-full">
							<Input
								value={searchValue}
								onChange={e => setSearchValue(e.target.value)}
								className="w-full !rounded-[8px] !pr-12 bg-white border-black/20"
								placeholder="Suchen..."
							/>
							{searchValue && (
								<X
									onClick={() => setSearchValue('')}
									className="size-5 opacity-50 hover:opacity-100 absolute top-1/2 -translate-y-1/2 right-8 lg:right-4 text-black cursor-pointer"
								/>
							)}
						</div>
					</div>
				</div>
				<div className="w-full mt-3 flex justify-center">
					<Button
						onClick={() => {
							setPage(1)
							setRowsPerPage(50)
							mutate({ searchValue, selectedColumn, page: 1, rowsPerPage: 50 })
						}}
						disabled={!searchValue || isLoading}
						className="rounded-lg w-fit lg:w-[150px] max-lg:py-1">
						{isLoading ? (
							<div className="flex items-center gap-2">
								<Loader2 className="animate-spin" />
								Suchen...
							</div>
						) : (
							'Suchen'
						)}
					</Button>
				</div>
			</div>
			{data?.data && (
				<div className="max-lg:p-4 max-lg:bg-white rounded-[20px] max-lg:mt-8 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
					<div className="flex max-lg:hidden items-center justify-between mt-6">
						<div className="flex items-center gap-4">
							<Select
								value={rowsPerPage.toString()}
								onValueChange={value => {
									setRowsPerPage(Number(value))
									mutate({ searchValue, selectedColumn, page, rowsPerPage: Number(value) })
								}}>
								<SelectTrigger className="w-[127px] bg-white">
									<SelectValue placeholder="zeigen 50" />
								</SelectTrigger>
								<SelectContent>
									{[50, 80, 100, 200, 500].map(pageSize => (
										<SelectItem key={pageSize} value={pageSize.toString()}>
											zeigen {pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-8">
							<PaginationComponent
								currentPage={page}
								totalPages={data?.pagination?.totalPages ?? 1}
								onPageChange={newPage => {
									setPage(newPage)
									mutate({ searchValue, selectedColumn, page: newPage, rowsPerPage })
								}}
							/>
						</div>
					</div>
					<div className="mt-4">
						{isSmallScreen ? (
							<MobileProductsTable />
						) : (
							<DataTable columns={columns} data={data?.data ?? []} isLoading={isLoading} />
						)}
					</div>
					<div className="mt-6 lg:mt-16 flex justify-center">
						<PaginationComponent
							currentPage={page}
							totalPages={data?.pagination.totalPages ?? 1}
							onPageChange={newPage => {
								setPage(newPage)
								mutate({ searchValue, selectedColumn, page: newPage, rowsPerPage })
							}}
						/>
					</div>
				</div>
			)}
			<Button
				onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
				variant="outline"
				size="icon"
				className={cn(
					'fixed bottom-6 rounded-lg right-6 z-50 text-white p-4 shadow-lg hover:opacity-90 duration-300 transition-opacity',
					showScrollTop ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
				)}>
				<ArrowUp className="text-black size-6" />
			</Button>
		</div>
	)
}
